import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataForm } from 'app/shared/services/data-order.service';
import { convertToLocalTime } from 'app/shared/helpers/utils';

export interface DialogDataFormViewData {
  form: DataForm;
}

export interface DataFormFileAnswer {
  id: number;
  url: string | null;
  name: string | null;
}

const STATUS_CONFIG: Record<string, { title: string; badge: string; icon: string; cssClass: string }> = {
  submitted: {
    title: 'Submitted Form',
    badge: 'Submitted',
    icon: 'assignment_turned_in',
    cssClass: 'df-modal__header--submitted',
  },
  pending: {
    title: 'Pending Form',
    badge: 'Pending',
    icon: 'pending_actions',
    cssClass: 'df-modal__header--pending',
  },
  expired: {
    title: 'Expired Form',
    badge: 'Expired',
    icon: 'event_busy',
    cssClass: 'df-modal__header--expired',
  },
};

@Component({
    selector: 'app-dialog-data-form-view',
    templateUrl: './dialog-data-form-view.component.html',
    styleUrls: ['./dialog-data-form-view.component.scss'],
    standalone: false
})
export class DialogDataFormViewComponent {
  constructor(
    public dialogRef: MatDialogRef<DialogDataFormViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataFormViewData,
  ) { }

  get form(): DataForm {
    return this.data.form;
  }

  get statusConfig() {
    return STATUS_CONFIG[this.form.status] || STATUS_CONFIG['pending'];
  }

  get answers(): Record<string, unknown> {
    return this.form.answers || {};
  }

  getAnswer(questionId: string): unknown {
    return this.answers[questionId] ?? null;
  }

  isFileAnswer(value: unknown): boolean {
    return Array.isArray(value) && value.length > 0 && typeof (value[0] as DataFormFileAnswer).id === 'number';
  }

  isStringArrayAnswer(value: unknown): boolean {
    return Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string');
  }

  asFileAnswers(value: unknown): DataFormFileAnswer[] {
    if (!Array.isArray(value)) return [];
    return value as DataFormFileAnswer[];
  }

  asStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value as string[];
  }

  asTextAnswer(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) return null;
    if (typeof value === 'object') return null;
    return String(value);
  }

  // Booking slot type handling
  isBookingSlotAnswer(value: unknown): boolean {
    if (value === null || value === undefined || Array.isArray(value)) return false;
    if (typeof value !== 'object') return false;
    const slot = value as Record<string, unknown>;
    return 'date' in slot || 'start_time' in slot || 'end_time' in slot;
  }

  formatBookingSlot(value: unknown): string {
    if (!this.isBookingSlotAnswer(value)) return '—';
    const slot = value as Record<string, string>;

    // Convert date to local time using utility helper
    let dateStr = '';
    if (slot['date']) {
      const localMoment = convertToLocalTime(slot['date']);
      if (localMoment) {
        dateStr = localMoment.format('ddd, D MMM YYYY');
      }
    }

    // Convert start/end times to local time
    let timeStr = '';
    if (slot['start_time'] && slot['end_time']) {
      const startLocal = convertToLocalTime(slot['start_time']);
      const endLocal = convertToLocalTime(slot['end_time']);
      if (startLocal && endLocal) {
        timeStr = `${startLocal.format('h:mm A')} – ${endLocal.format('h:mm A')}`;
      }
    }

    return [dateStr, timeStr].filter(Boolean).join(' at ') || '—';
  }

  // Consent type handling
  isConsentConfirmed(value: unknown): boolean {
    return value === 'confirmed' || value === true || value === 'true' || value === 'yes' || value === 'Yes';
  }

  formatConsentAnswer(value: unknown): string {
    if (this.isConsentConfirmed(value)) return 'Confirmed';
    if (value === false || value === 'false' || value === 'no' || value === 'No') return 'Not confirmed';
    return String(value);
  }

  close(): void {
    this.dialogRef.close();
  }
}
